<template>
  <component
    :is="tag"
    class="fish-button"
    :class="[`fish-button--${variante}`, `fish-button--${tamanho}`, { 'fish-button--bloco': bloco }]"
    :type="tag === 'button' ? tipo : null"
    :disabled="tag === 'button' ? desabilitado || carregando : null"
    :aria-disabled="desabilitado || carregando ? 'true' : null"
    :aria-busy="carregando ? 'true' : null"
    @click="aoClicar"
  >
    <i v-if="carregando" class="fish-button__girando girando" aria-hidden="true" />
    <i v-else-if="icone" class="fish-button__icone" :class="`fa-${icone}`" aria-hidden="true" />

    <span class="fish-button__texto"><slot /></span>

    <!--
      O motivo do bloqueio, no PRÓPRIO botão (D11, 7.4e).

      "O que está bloqueado diz o que falta, não só que está bloqueado." Um botão morto sem
      explicação faz o jogador achar que o jogo quebrou; um que diz "Nível 10 necessário" faz ele
      saber o que fazer. É a diferença entre um obstáculo e um objetivo.
    -->
    <small v-if="motivo" class="fish-button__motivo">{{ motivo }}</small>
  </component>
</template>

<script>
/**
 * 2.2 — `FishButton`, a placa de ação (UI-14, UI-15, D4, D15).
 *
 * ## Os dois problemas que ele resolve
 *
 * **UI-14 — os botões não pareciam do jogo.** `<button>` não herda `font-family` (é regra do CSS), e
 * como poucos componentes declaravam, "Explorar Clãs", "Ver Ranking", "Melhorar" e "Comprar"
 * renderizavam na sans do sistema. O `base.css` já corrige isso globalmente; aqui a fonte é
 * explícita de novo porque um primitivo não deve depender de um reset para estar certo.
 *
 * **UI-15 — cada tela inventou sua paleta.** Só na tela de Clã havia três gradientes distintos lado
 * a lado: laranja, ciano e roxo-rosa. Não existiam papéis. Agora existem **três, e só três**:
 *
 * | Variante | Quando | Cor |
 * |---|---|---|
 * | `primario` | a ação principal da tela. **Uma por tela** (D6) | latão |
 * | `neutro` | tudo o mais | madeira |
 * | `perigo` | o que gasta dinheiro real ou destrói item | terracota |
 *
 * Não há uma quarta. Se uma tela parece precisar de mais um papel, o que ela tem é ação demais (D6).
 *
 * ## O detalhe que tira a cara de site (D4)
 *
 * **O estado apertado é ARTE, não filtro CSS.** O `:active` troca `botao-primario.png` por
 * `botao-primario-apertado.png` — que é um desenho diferente, sem o bisel de luz em cima — e desce
 * o botão 2px. Pixel art não faz sombra interna; ela desce.
 *
 * O squash é de **4%** (D15), não 20%. Quique grande pertence a jogo rápido; aqui seria um estranho
 * na tela — e o D0 veta o que grita.
 */
import { tocar } from './som';

export default {
  name: 'FishButton',
  props: {
    variante: {
      type: String,
      default: 'neutro',
      validator: (v) => ['primario', 'neutro', 'perigo'].includes(v),
    },
    tamanho: {
      type: String,
      default: 'medio',
      validator: (v) => ['pequeno', 'medio', 'grande'].includes(v),
    },
    /** Classe do ícone, sem o `fa-`. O `pixel-icons.css` a transforma em sprite (D20). */
    icone: { type: String, default: '' },
    desabilitado: { type: Boolean, default: false },
    carregando: { type: Boolean, default: false },
    /** Ocupa a largura toda. É o padrão da ação principal na base da tela (D11). */
    bloco: { type: Boolean, default: false },
    /** O que falta, escrito no botão (D11). Ex.: "Nível 10 necessário", "Só o líder ativa". */
    motivo: { type: String, default: '' },
    tag: { type: String, default: 'button' },
    tipo: { type: String, default: 'button' },
    /**
     * Qual som o clique toca (UI-22). `clique` por padrão; `compra` na confirmação de uma compra;
     * `''` para silêncio, quando a ação já dispara outro som logo em seguida.
     */
    som: { type: String, default: 'clique' },
  },
  emits: ['click'],
  methods: {
    aoClicar(evento) {
      if (this.desabilitado || this.carregando) {
        /**
         * O clique num botão bloqueado não é ignorado em silêncio (UI-22, D11).
         *
         * Ele toca o som de erro — que **não é um buzzer**, é duas notas descendo (D0) — e avisa
         * quem estiver ouvindo, para a tela poder explicar o motivo com um balão ou um comentário
         * do NPC. Silêncio total é o que faz o jogador clicar cinco vezes achando que quebrou.
         */
        tocar('erro');
        this.$emit('bloqueado', this.motivo);
        evento.preventDefault();
        return;
      }

      /**
       * O clique mora AQUI, e é a correção do UI-22.
       *
       * Havia 210 `@click` no cliente e nenhum tocava som. Pedir que cada tela lembre de tocar é
       * garantir que 210 lugares esqueçam — foi assim que se chegou ao estado atual. No primitivo,
       * ele existe para todo botão do jogo sem ninguém precisar lembrar.
       *
       * `som` é a prop de escape: a ação principal de uma compra passa `som="compra"`, e quem
       * quiser silêncio (um botão que já dispara outro som logo em seguida) passa `som=""`.
       */
      if (this.som) tocar(this.som);
      this.$emit('click', evento);
    },
  },
};
</script>

<style scoped>
.fish-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-2);
  position: relative;

  font-family: var(--fonte-display);
  line-height: var(--lh-curto);
  text-align: center;
  text-decoration: none;

  /**
   * A PLACA, do Book UI System (D2).
   *
   * `border-image` com a fatia `9 6 14 6` medida no PNG. As fatias são assimétricas porque a arte
   * é: 9px em cima cobrem a aresta e o colchete de canto; **14px embaixo cobrem o colchete E o
   * lábio** — a espessura da placa vista de cima, que é o que faz o botão parecer um objeto de
   * madeira e não um retângulo pintado.
   *
   * `fill` **não** é usado de propósito: o miolo do PNG é opaco, e deixá-lo preencher impediria o
   * `background` de mostrar a cor do token. Assim a arte dá a moldura e o token dá o corpo, e os
   * dois combinam porque saem do mesmo lugar (ver "papéis de superfície" em `tokens.css`).
   */
  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/botao-neutro.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background-clip: padding-box;
  image-rendering: pixelated;

  min-height: var(--alvo-minimo); /* D11 — nunca menor que o dedo */
  padding: var(--e-1) var(--e-3);
  cursor: pointer;

  /* Nunca `transition: all` (UI-24): ele anima também propriedades de layout e força reflow a cada
   * quadro, que é a causa mais comum de travamento em animação CSS. Só as duas que mudam. */
  transition: transform var(--t-instantaneo) var(--curva-quique),
    filter var(--t-rapido) var(--curva-suave);
}

.fish-button--bloco {
  display: flex;
  width: 100%;
}

.fish-button--pequeno {
  font-size: var(--f-2);
  padding: var(--e-1) var(--e-3);
}

.fish-button--medio {
  font-size: var(--f-3);
}

.fish-button--grande {
  font-size: var(--f-4);
  min-height: 56px;
  padding: var(--e-3) var(--e-5);
}

/* --- As três variantes ------------------------------------------------------------------------ */

/**
 * As três variantes. A cor de fundo é **exatamente** a cor de preenchimento do PNG correspondente —
 * é isso que faz a emenda entre a borda-imagem e o miolo sumir sem ninguém alinhar pixel.
 */
.fish-button--primario {
  border-image-source: url('/ui/botao-primario.png');
  background: var(--ouro);
  color: var(--ouro-escuro); /* tinta escura sobre latão: é o único par que passa em contraste */
}

.fish-button--neutro {
  border-image-source: url('/ui/botao-neutro.png');
  background: var(--madeira);
  color: var(--papel-luz);
}

.fish-button--perigo {
  border-image-source: url('/ui/botao-perigo.png');
  background: var(--cor-ruim);
  color: var(--papel-luz);
}

/* --- O toque (D15) --------------------------------------------------------------------------- */

/* O hover só existe onde há ponteiro (UI-26). Em toque ele grudava depois do tap e só saía ao tocar
 * em outro lugar — com 215 declarações no cliente, isso acontecia o tempo todo no celular. */
@media (hover: hover) {
  .fish-button:not(:disabled):hover {
    filter: brightness(1.12);
  }
}

/**
 * O apertado: a ARTE troca e o botão DESCE 3px.
 *
 * A placa apertada é um desenho diferente — perde a linha de luz na aresta de cima e escurece o
 * corpo. Descer 3px é a altura do lábio: visualmente a placa **afunda até encostar na madeira**,
 * que é exatamente o que uma placa pregada faz. Só trocar a cor faria "apagou"; descer faz
 * "afundou" (D4).
 *
 * Sem `scale`: a placa é retangular e rígida. O squash de 4% do D15 é para o que é macio — ele
 * vive no `FishSlot` e no ícone, não aqui.
 */
.fish-button:not(:disabled):active {
  transform: translateY(3px);
}

.fish-button--primario:not(:disabled):active {
  border-image-source: url('/ui/botao-primario-apertado.png');
  background: var(--latao);
}

.fish-button--neutro:not(:disabled):active {
  border-image-source: url('/ui/botao-neutro-apertado.png');
  background: var(--madeira-sombra);
}

.fish-button--perigo:not(:disabled):active {
  border-image-source: url('/ui/botao-perigo-apertado.png');
  background: #7d3f2e;
}

/* O bloqueado NÃO fica invisível: ele precisa ser lido, porque é ele que carrega o motivo (D11). */
.fish-button:disabled,
.fish-button[aria-disabled='true'] {
  filter: grayscale(0.6);
  opacity: 0.62;
  cursor: not-allowed;
}

.fish-button__icone {
  font-size: 1.25em;
  flex-shrink: 0;
}

.fish-button__girando {
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: var(--r-circulo);
  flex-shrink: 0;
}

.fish-button__texto {
  min-width: 0;
}

/* O motivo mora ABAIXO do rótulo, dentro do próprio botão. Assim ele acompanha a ação em vez de
 * virar uma legenda solta que a próxima refatoração de layout separa dela.
 *
 * O `- 7px` é o LÁBIO da placa (`--placa-borda` é `5px 6px 7px 6px`). `bottom` mede do bloco
 * contentor, que num elemento com borda é a caixa de PADDING — dentro da borda. Sem descontar o
 * lábio, a metade de cima da legenda ficava escondida atrás da aresta de baixo do próprio botão:
 * medido em 04/09 no modal do armazém, o texto começava 7px acima da borda e sumia nela. */
.fish-button__motivo {
  position: absolute;
  bottom: calc(-1.25em - 7px);
  left: 0;
  right: 0;
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--cor-aviso);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
